import { useState, useRef, useEffect } from "react";
import { managerAIApi , trainingSessionReport } from "../services/apiService";
import { v4 as uuidv4 } from 'uuid';
import VoiceRecorder, { VoiceRecorderHandle } from "../components/VoiceRecorder";

interface Message {
  sender: "user" | "bot" | "loading";
  text: string;
}

function ManagerAI() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [current_user_chat_code, setCurrent_user_chat_code] = useState("");
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimeoutRef2 = useRef<NodeJS.Timeout | null>(null);
  const [sessionExpired,setSessionExpired] = useState(false)
  const [wholeLoading, setWholeLoading] = useState(false);
  const latestMessagesRef = useRef<Message[]>([]);
  const [count1, setCount1] = useState(0);
  const [count2, setCount2] = useState(0);
   const count1Ref = useRef(0);
   const count2Ref = useRef(0);

  const voiceRecorderRef = useRef<VoiceRecorderHandle>(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    setCurrent_user_chat_code(uuidv4());

    const init = async () => {
        setWholeLoading(true);
        await nm("GIVE#INTRO");
        setWholeLoading(false);
        setCurrent_user_chat_code(uuidv4());
    };

    init();
  }, []);

    useEffect(() => {
        const lastMsg = messages[messages.length - 1];
        if (lastMsg?.sender !== "bot" || sessionExpired === true){
            return;
        } 

        const interval = setInterval(() => {
            if (count1Ref.current === 4) {
            setMessages((prev) => [...prev, {
                sender: "bot",
                text: "You haven’t responded in the last 5 minutes. Please reply to continue. If you do not respond within 2 minutes, the session may be ended."
            }]);
            }
            count1Ref.current += 1;
        }, 60000);

        return () => clearInterval(interval);
    }, [messages]);

    useEffect(() => {
        const lastMsg = messages[messages.length - 1];
        if (lastMsg?.sender !== "bot" || sessionExpired === true){
            return;
        } 

        const interval = setInterval(() => {
            nm("NRFUF2M");
        }, 120000);

        return () => clearInterval(interval);
    }, [messages]);


    useEffect(() => {
        const lastMsg = messages[messages.length - 1];
        if (lastMsg?.sender !== "bot" || sessionExpired === true){
            count2Ref.current = 0
            return;
        } 

        const interval = setInterval(() => {
            if(count2Ref.current === 6){
                alert("Session Expired")
                setMessages((prev) => [...prev, { sender: "bot", text: "Session Expired" }]);
                setSessionExpired(true)
                trainingSessionReport()
            }
            count2Ref.current += 1;    
        }, 60000);

        return () => clearInterval(interval);
    }, [messages]);

    useEffect(() => {
        latestMessagesRef.current = messages;
    }, [messages]);

    async function nm(code:string){
        const formData = new FormData();
        formData.append('userQuery', code);
        formData.append('current_user_chat_code', "123");
        const response = await managerAIApi(formData);
        const botReply = response.reply || "No response";
        setMessages((prev) => {
            const filtered = prev.filter(msg => msg.text !== "🤖 Getting response...");
            return [...filtered, { sender: "bot", text: botReply }];
        });
    }


  const handleBotReply = async (userInput: string, audioBlob?: Blob) => {
    try {
      setLoading(true);

      const formData = new FormData();
      let audio = false;

      voiceRecorderRef.current?.stopRecording();

      if (audioBlob) {
        formData.append('audio', audioBlob, 'voice.webm');
        formData.append('userQuery', "");
        setMessages(prev => [...prev, { sender: "user", text: "🎤 (voice message sent)" }]);
        setMessages(prev => [...prev, { sender: "loading", text: "🤖 Getting response..." }]);
        audio = true;
      } else {
        formData.append('userQuery', userInput);
        const userMessage: Message = { sender: "user", text: input };
        setMessages((prev) => [...prev, userMessage]);
        setMessages((prev) => [...prev, { sender: "loading", text: "🤖 Getting response..." }]);
      }

      formData.append('current_user_chat_code', current_user_chat_code);

      const response = await managerAIApi(formData);
      const userAudioText = response.transcribed_text;
      const botReply = response.reply || "No response";

      if (audio) {
        setMessages((prev: any) => {
          const updated = prev.map((msg: any, index: number) => {
            if (index === prev.length - 2) {
              return { ...msg, text: `🎤 (voice message sent) - ${userAudioText}` };
            } else {
              return msg;
            }
          });
          return [...updated];
        });
      }

      setMessages((prev) => {
        const filtered = prev.filter(msg => msg.text !== "🤖 Getting response...");
        return [...filtered, { sender: "bot", text: botReply }];
      });

      if (response.role !== "") {
        localStorage.setItem('userBotRole', response.role);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Sorry, something went wrong." },
      ]);
      setMessages((prev) => {
        const filtered = prev.filter(msg => msg.text !== "🤖 Getting response...");
        return [...filtered];
      });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (audioBlob?: Blob) => {
    const currentInput = input;
    setInput("");
    await handleBotReply(currentInput, audioBlob);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") sendMessage();
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div className="w-full h-screen flex flex-col bg-white">
      <div className="bg-blue-600 text-white text-center py-4 text-xl font-semibold shadow-md">
        TRAINING AI 
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4 w-full">
        {messages.map((msg, index) => (
            <div
                key={index}
                className={`px-4 py-3 rounded-lg whitespace-pre-line ${
                    msg.sender === "user"
                    ? "bg-blue-500 text-white w-fit max-w-[50%] self-end ml-auto"
                    : msg.sender === "loading"
                    ? "bg-white-500 text-black self-start mr-auto"
                    : "bg-gray-100 text-black w-fit max-w-[70%] self-start mr-auto"
                }`}
                dangerouslySetInnerHTML={{
                    __html:
                    msg.text === "🤖 Getting response..."
                        ? `<div class="flex items-center space-x-2">
                            <span class="text-sm">Getting Response</span>
                            <div class="flex space-x-1">
                            <div class="h-2 w-2 bg-gray-600 rounded-full animate-bounce" style="animation-delay: 0s;"></div>
                            <div class="h-2 w-2 bg-gray-600 rounded-full animate-bounce" style="animation-delay: 0.1s;"></div>
                            <div class="h-2 w-2 bg-gray-600 rounded-full animate-bounce" style="animation-delay: 0.2s;"></div>
                            </div>
                        </div>`
                        : msg.text,
                }}
            />
        ))}
        <div ref={chatEndRef} />
      </div>

      <div className="flex border-t border-gray-300 p-4 bg-white">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={sessionExpired ? "Session Expired" : "Type your message..."}
          className={`flex-1 px-4 py-2 border me-2 rounded-l-md focus:outline-none ${sessionExpired && 'bg-gray-500'} `}
          disabled={sessionExpired}
        />
        <div className="flex items-center space-x-2">
          <button
            onClick={() => sendMessage()}
            disabled={loading}
            className={`px-5 py-2 rounded-r-md text-white transition-colors duration-200 ${
              (loading || sessionExpired) ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {sessionExpired ? "" : loading ? "Sending..." : "Send"}
          </button>
          <VoiceRecorder
            ref={voiceRecorderRef}
            onAudioRecorded={(audioBlob: Blob) => sendMessage(audioBlob)}
            disabled={loading || sessionExpired}
          />
        </div>
      </div>
      {
        wholeLoading && <div style={{position:'fixed',top:'0',bottom:'0',width:'100%',display:'flex',alignItems:'center',justifyContent:'center',backgroundColor: 'rgba(0, 0, 0, 0.5)'}}>
                            <div style={{background:'white',border:' 2px solid #0050C8'}} className="flex items-center rounded px-7 py-3">
                                <div className="spinner me-2"></div>
                                <p className="text-blue-700">Preparing your chat please wait...</p> 
                            </div>
                        </div>
      }
    </div>
  );
}

export default ManagerAI;